// frontend/src/components/chatbot/ChatbotSidebar.jsx
import React, { useState, useRef, useEffect } from 'react';
import useChatbot from './useChatbot';
import ChatResponse from './ChatResponse';
import { X, Send, Trash2, RotateCcw, ThumbsUp, ThumbsDown, AlertCircle } from 'lucide-react';
import { API_BASE } from '../../config/api';
import { httpRequest } from '../../utils/httpClient';
import { authService } from '../../services/auth';

const ChatbotSidebar = () => {
  const { isOpen, closeChat, messages, sendMessage, isLoading, clearHistory, rateMessage } = useChatbot();
  const [ratedIds, setRatedIds] = useState(new Set());
  const [input, setInput] = useState('');
  const [showErrorReport, setShowErrorReport] = useState(false);
  const [errorReportText, setErrorReportText] = useState('');
  const [errorReportStatus, setErrorReportStatus] = useState(null);
  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Focus textarea when opened
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => textareaRef.current?.focus(), 100);
    }
  }, [isOpen]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!input.trim() || isLoading) return;
    sendMessage(input.trim());
    setInput('');
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  const handleRetry = (msg) => {
    if (msg.role === 'user') {
      sendMessage(msg.content);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="chatbot-sidebar-overlay" onClick={closeChat}>
      <aside
        className="chatbot-sidebar"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-label="Pista app helper"
      >
        {/* Header */}
        <div className="chatbot-sidebar-header">
          <div className="chatbot-sidebar-title">
            <img src="/logo192.png" alt="Pista" className="chatbot-brand-icon" />
            <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              Pista app helper
              <span style={{ fontSize: '0.65em', padding: '0.15em 0.4em', borderRadius: '4px', background: 'var(--primary-color, #1976d2)', color: 'white', fontWeight: 500, verticalAlign: 'middle' }}>beta</span>
            </span>
          </div>
          <div className="chatbot-sidebar-actions">
            <button
              type="button"
              onClick={clearHistory}
              className="chatbot-header-btn"
              title="Clear chat"
              aria-label="Clear chat"
            >
              <Trash2 size={16} />
            </button>
            <button
              type="button"
              onClick={closeChat}
              className="chatbot-header-btn"
              title="Close"
              aria-label="Close support chat"
            >
              <X size={18} />
            </button>
          </div>
        </div>

        {/* Messages */}
        <div className="chatbot-messages">
          {messages.length === 0 && (
            <div className="chatbot-welcome">
              <p>Hi! I am Pista, ask me anything about using the app.</p>
            </div>
          )}

          {messages.map((msg) => (
            <div
              key={msg.id}
              className={`chatbot-message ${msg.role} ${msg.error ? 'error' : ''}`}
            >
              <div className="chatbot-message-bubble">
                {msg.content ? (
                  msg.role === 'assistant' ? (
                    <ChatResponse content={msg.content} query={msg.query} />
                  ) : (
                    <div className="chatbot-message-text">{msg.content}</div>
                  )
                ) : (
                  <div className="chatbot-message-text chatbot-typing">
                    <span className="dot" />
                    <span className="dot" />
                    <span className="dot" />
                  </div>
                )}

                {/* Source citations */}
                {msg.sources && msg.sources.length > 0 && (
                  <div className="chatbot-sources">
                    {msg.sources.map((src, idx) => (
                      <button
                        key={idx}
                        type="button"
                        className="chatbot-source-badge"
                        title={src}
                        onClick={() => {/* TODO: open source detail */}}
                      >
                        {src}
                      </button>
                    ))}
                  </div>
                )}

                {/* Retry for error messages */}
                {msg.error && msg.role === 'assistant' && (
                  <button
                    type="button"
                    className="chatbot-retry-btn"
                    onClick={() => {
                      const idx = messages.findIndex(m => m.id === msg.id);
                      const prev = idx > 0 ? messages[idx - 1] : null;
                      if (prev && prev.role === 'user') handleRetry(prev);
                    }}
                  >
                    <RotateCcw size={12} /> Retry
                  </button>
                )}

                {/* Thumbs up/down for assistant messages */}
                {!msg.error && msg.role === 'assistant' && !ratedIds.has(msg.id) && (
                  <div className="chatbot-rating">
                    <button
                      type="button"
                      className="chatbot-rating-btn"
                      title="Helpful"
                      aria-label="Mark as helpful"
                      onClick={() => {
                        rateMessage(msg.id, true);
                        setRatedIds(prev => new Set(prev).add(msg.id));
                      }}
                    >
                      <ThumbsUp size={14} />
                    </button>
                    <button
                      type="button"
                      className="chatbot-rating-btn"
                      title="Not helpful"
                      aria-label="Mark as not helpful"
                      onClick={() => {
                        rateMessage(msg.id, false);
                        setRatedIds(prev => new Set(prev).add(msg.id));
                      }}
                    >
                      <ThumbsDown size={14} />
                    </button>
                  </div>
                )}
                {ratedIds.has(msg.id) && (
                  <span className="chatbot-rated-label">Thanks for your feedback</span>
                )}
              </div>
            </div>
          ))}

          {isLoading && messages.length > 0 && messages[messages.length - 1].role === 'user' && (
            <div className="chatbot-message assistant">
              <div className="chatbot-message-bubble">
                <div className="chatbot-message-text chatbot-typing">
                  <span className="dot" />
                  <span className="dot" />
                  <span className="dot" />
                </div>
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* Error Report Form */}
        {showErrorReport && (
          <div style={{
            padding: '0.75rem 1rem',
            borderTop: '1px solid var(--border-color, #e0e0e0)',
            backgroundColor: 'var(--bg-secondary, #f9f9f9)'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
              <span style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-primary, #333)' }}>
                Report an Error
              </span>
              <button
                type="button"
                onClick={() => { setShowErrorReport(false); setErrorReportText(''); setErrorReportStatus(null); }}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-secondary, #666)' }}
                aria-label="Cancel error report"
              >
                <X size={14} />
              </button>
            </div>
            <textarea
              value={errorReportText}
              onChange={(e) => setErrorReportText(e.target.value)}
              placeholder="Describe the error or issue you encountered..."
              rows={3}
              style={{
                width: '100%',
                resize: 'vertical',
                padding: '0.5rem',
                borderRadius: '6px',
                border: '1px solid var(--border-color, #ccc)',
                fontSize: '0.8rem',
                fontFamily: 'inherit',
                marginBottom: '0.5rem'
              }}
            />
            {errorReportStatus && (
              <div style={{ fontSize: '0.75rem', marginBottom: '0.5rem', color: errorReportStatus.type === 'success' ? 'var(--pista-green-primary, #2E7D32)' : '#d32f2f' }}>
                {errorReportStatus.message}
              </div>
            )}
            <button
              type="button"
              disabled={!errorReportText.trim()}
              onClick={async () => {
                setErrorReportStatus({ type: 'info', message: 'Sending...' });
                try {
                  const headers = { 'Content-Type': 'application/json' };
                  const token = authService.getToken();
                  if (token) headers['Authorization'] = `Bearer ${token}`;
                  const res = await httpRequest(`${API_BASE}/api/chat/error-report`, {
                    method: 'POST',
                    headers,
                    body: JSON.stringify({
                      description: errorReportText.trim(),
                      page_url: window.location.href,
                      user_agent: navigator.userAgent
                    })
                  });
                  if (res.ok) {
                    setErrorReportStatus({ type: 'success', message: 'Report sent. Thank you!' });
                    setErrorReportText('');
                    setTimeout(() => { setShowErrorReport(false); setErrorReportStatus(null); }, 2000);
                  } else {
                    setErrorReportStatus({ type: 'error', message: 'Failed to send report. Please try again.' });
                  }
                } catch (err) {
                  setErrorReportStatus({ type: 'error', message: 'Network error. Please try again later.' });
                }
              }}
              style={{
                padding: '0.4rem 0.75rem',
                borderRadius: '6px',
                border: 'none',
                backgroundColor: 'var(--pista-green-primary, #2E7D32)',
                color: 'white',
                fontSize: '0.8rem',
                fontWeight: 600,
                cursor: errorReportText.trim() ? 'pointer' : 'not-allowed',
                opacity: errorReportText.trim() ? 1 : 0.5
              }}
            >
              Submit Report
            </button>
          </div>
        )}

        {/* Input */}
        <form className="chatbot-input-area" onSubmit={handleSubmit}>
          <textarea
            ref={textareaRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Ask a question..."
            rows={1}
            disabled={isLoading}
            className="chatbot-textarea"
          />
          <button
            type="submit"
            disabled={!input.trim() || isLoading}
            className="chatbot-send-btn"
            aria-label="Send message"
          >
            <Send size={18} />
          </button>
          <button
            type="button"
            onClick={() => setShowErrorReport(!showErrorReport)}
            className="chatbot-header-btn"
            title="Report an error"
            aria-label="Report an error"
            style={{ flexShrink: 0 }}
          >
            <AlertCircle size={16} />
          </button>
        </form>
      </aside>
    </div>
  );
};

export default ChatbotSidebar;
